import { DATAFEED_API } from '../../config/index.js'

export interface SymbolListItem {
  symbol: string
  description?: string | null
  type?: string | null
  point_size?: number | null
}

export interface SymbolMeta extends SymbolListItem {
  /** ISO date (YYYY-MM-DD) of the earliest available data, if known. */
  date_start?: string | null
  /** ISO date (YYYY-MM-DD) of the latest available data, if known. */
  date_end?: string | null
}

const FETCH_TIMEOUT_MS = 15_000

let symbolsCache: SymbolListItem[] | null = null

/** List enabled datafeed symbols (cached for the session). */
export async function fetchSymbols(): Promise<SymbolListItem[]> {
  if (symbolsCache) return symbolsCache
  const res = await fetch(`${DATAFEED_API}/datafeed/symbols/`, {
    signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
  })
  if (!res.ok) throw new Error(`Failed to load symbols (${res.status})`)
  const data: SymbolListItem[] = await res.json()
  symbolsCache = data
  return data
}

/** Metadata for one symbol, including the available data date range. */
export async function fetchSymbolMeta(symbol: string): Promise<SymbolMeta | null> {
  const res = await fetch(`${DATAFEED_API}/datafeed/symbol/${encodeURIComponent(symbol)}`, {
    signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
  })
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`Failed to load symbol meta (${res.status})`)
  return res.json()
}

/**
 * Decimal places implied by a symbol's point size (0.00001 -> 5). Drives chart
 * price formatting AND klinecharts' y-axis autoscale padding, which pads the
 * range by 10^-precision when the visible spread is tiny — a too-coarse value
 * squashes quiet windows into a thin strip. Null when point size is unknown.
 */
export function precisionFromPointSize(pointSize: number | null | undefined): number | null {
  if (!pointSize || pointSize <= 0) return null
  const decimals = Math.round(-Math.log10(pointSize))
  return Number.isFinite(decimals) && decimals >= 0 ? decimals : null
}
